import { useEffect, useState } from "react";
import FluxiLogo from "../assets/svg/FluxiLogoWhite.svg";
import GoogleLoginButton from "../components/auth/GoogleLoginButton";
import CodeVerificationStep from "../components/auth/steps/CodeVerificationStep";
import EmailStep from "../components/auth/steps/EmailStep";
import { useSearchParams } from "react-router-dom";
import { dictionary, Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import CreateAccountStep from "../components/auth/steps/CreateAccountStep";
import {
  onboardingStatusService,
  buildPendingUserDetailFromForm,
  setPendingUserDetail,
} from "@/feature/auth";
import { postSendCodeEmail } from "../services/auth/authAxios/authAxios";
import { toast } from "react-hot-toast";

export interface UserData {
  email: string;
  verificationCode: string;
}

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const { language, setLanguage } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [userData, setUserData] = useState<UserData>({
    email: '',
    verificationCode: ''
  });
  const [createAccountError, setCreateAccountError] = useState<string>("");
  const [isCreateAccountLoading, setIsCreateAccountLoading] = useState(false);
  const [onboardingReason, setOnboardingReason] = useState<string | null>(null);

  useEffect(() => {
    if (email) {
      setCurrentStep(3)
      setUserData({
        email: email,
        verificationCode: ''
      })
    }
  }, [email]);

  const updateUserData = (data: Partial<UserData>) => {
    setUserData(prev => ({ ...prev, ...data }));
  };

  const goToStep = (step: 1 | 2 | 3) => setCurrentStep(step);

  const sendVerificationCodeAndGoToStep3 = async (emailToSend: string) => {
    const res = await postSendCodeEmail({ email: emailToSend });
    toast.success(res.message);
    updateUserData({ email: res.email });
    goToStep(3);
  };

  const handleClose = () => {
    setCurrentStep(1);
    setCreateAccountError("");
    setIsCreateAccountLoading(false);
    setOnboardingReason(null);
    setUserData({
      email: "",
      verificationCode: "",
    });
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <EmailStep
            onClose={handleClose}
            email={userData.email}
            onNext={async (email) => {
              updateUserData({ email });
              setCreateAccountError("");

              try {
                const status = await onboardingStatusService.getByEmail(email);
                if (status.requires_onboarding) {
                  setOnboardingReason(status.reason ?? null);
                  goToStep(2);
                  return;
                }
              } catch {
                // Si este servicio falla, no bloqueamos el login.
              }

              await sendVerificationCodeAndGoToStep3(email);
            }}
          />
        );
      case 2:
        return (
          <CreateAccountStep
            email={userData.email}
            onboardingReason={onboardingReason}
            isLoading={isCreateAccountLoading}
            error={createAccountError}
            onBack={() => goToStep(1)}
            onSubmit={async ({ fullName, phoneNumber }) => {
              if (!fullName.trim()) {
                setCreateAccountError(
                  dictionary["name-required"]?.[language as Language] ??
                    "El nombre es requerido"
                );
                return;
              }
              if (!phoneNumber.trim()) {
                setCreateAccountError(
                  dictionary["phone-required"]?.[language as Language] ??
                    "El celular es requerido"
                );
                return;
              }

              setCreateAccountError("");
              setIsCreateAccountLoading(true);

              try {
                setPendingUserDetail(
                  buildPendingUserDetailFromForm({
                    fullName,
                    phoneNumber,
                  })
                );
                await sendVerificationCodeAndGoToStep3(userData.email);
              } catch (err: unknown) {
                toast.error(err instanceof Error ? err.message : "No pudimos enviar el código");
              } finally {
                setIsCreateAccountLoading(false);
              }
            }}
          />
        );
      case 3:
        return (
          <CodeVerificationStep
            onClose={handleClose}
            email={userData.email}
            onNext={(code) => {
              updateUserData({ verificationCode: code });
            }}
            onBack={() => goToStep(1)}
          />
        );
      default:
        return null;
    }
  };

  const handleChangeLanguage = (lang: Language) => {
    setLanguage(lang)
    localStorage.setItem("user_language", lang)
  }
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-no-repeat"
      style={{
        backgroundImage: "url('/assets/images/login-background.webp')",
      }}
    >
      {/* Language switcher top right */}
      <div className="absolute top-6 right-6 z-50">
        <LanguageSwitcher
          currentLanguage={language}
          languages={Object.values(Language)}
          onChangeLanguage={handleChangeLanguage}
        />
      </div>
      <div
        className={`flex flex-row max-md:flex-col max-md:gap-8 w-full max-w-[1200px] h-auto ${currentStep === 2 ? "md:h-[90vh]" : "md:h-[500px]"
          } rounded-2xl ${currentStep === 2 ? "overflow-visible" : "overflow-hidden"} bg-opacity-90 px-4 md:px-0`}
      >
        {/* Left Section */}
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center px-4 md:px-8 text-white py-8 md:py-0">
          <div className="text-center text-white">
            <h1 className="text-[32px] md:text-[32px] lg:text-[36px] font-bold mb-1 leading-tight pt-0 md:pt-0">
              <span className="font-semibold">
                {dictionary["sales-by-"]?.[language as Language] ?? "Ventas como por"}
              </span>
            </h1>
            <h2 className="text-[32px] md:text-[28px] lg:text-[32px] italic font-light leading-tight " style={{ fontFamily: "'Playfair Display', serif", lineHeight: "1" }}>
              <span className="italic text-[#85A2FF]">{dictionary["magic-art"]?.[language as Language] ?? "arte de magia"}</span>
            </h2>
          </div>
        </div>

        {/* Right Section */}
        <div
          className={`w-full md:w-[474px] max-w-[90%] p-6 md:px-[64px] md:py-[20px] flex flex-col items-center ${currentStep === 2 ? "justify-start md:h-full md:max-h-[calc(90vh-40px)] md:overflow-auto" : "justify-center"} gap-4 rounded-[20px] border-2 border-[#85A2FF] backdrop-blur-md mx-auto md:mx-0 min-h-[300px] md:h-auto overflow-visible ${currentStep === 2 ? "md:overflow-auto" : "md:overflow-hidden"
            }`}
          style={{
            background: "rgba(255, 255, 255, 0.08)",
            boxSizing: "border-box",
          }}
        >
          <img
            src={FluxiLogo}
            alt="Fluxi Logo"
            className="h-16 md:h-auto mb-2"
          />

          {/* Title Section */}
          {currentStep === 1 && <div className="flex flex-col items-center gap-1">
            <h2 className="text-[24px] md:text-[30px] font-bold text-white text-center">
              {dictionary["enter-your-email"]?.[language as Language] ?? "Ingresa tu correo"}
            </h2>
          </div>}
          {renderStep()}
          {(currentStep === 1 || currentStep === 2) && (
            <>
              <div className="flex flex-col items-center justify-center w-[120px]" style={{ border: "1px solid #85A2FF" }}>
              </div>
              <p className="m-0 text-white text-sm">{dictionary["or-sign-in-with"]?.[language as Language] ?? "O ingresa con"}:</p>
              <GoogleLoginButton />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
